import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import CallToAction from '../calltoaction/CallToAction';
import Image from '../../../../assets/domestic.webp';
import Button from '../../../common/Button';
import ExploreIcon from '../../../../assets/Shine.svg';
import { slugify, deslugify } from '../../../utils/slug';
import { fetchDomesticPackages } from '../../../utils/api';

const StatePage = () => {
  const { stateSlug } = useParams();
  const stateName = stateSlug ? deslugify(stateSlug) : null;
  const [favorites, setFavorites] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [bannerImage, setBannerImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const MINIMUM_LOAD_TIME = 0;

    const loadSubcategories = async () => {
      const startTime = Date.now();
      try {
        setLoading(true);
        const data = stateSlug
          ? await fetchDomesticPackages({ categorySlug: stateSlug })
          : await fetchDomesticPackages({ type: 'category' });
        if (stateSlug) {
          const category = data.find(
            (item) => item.type === 'category' && slugify(item.name || '') === stateSlug,
          );
          if (category && category.bannerImage) {
            setBannerImage(`${process.env.REACT_APP_API_BASE_URL}/${category.bannerImage}`);
          }

          const filteredSubcategories = data
            .filter(
              (item) =>
                item.type === 'subcategory' && slugify(item.category?.name || '') === stateSlug,
            )
            .map((item) => ({
              _id: item._id,
              title: item.title,
              image: `${process.env.REACT_APP_API_BASE_URL}/${item.image}`,
            }));
          setSubcategories(filteredSubcategories);
        } else {
          const categories = data
            .filter((item) => item.type === 'category')
            .map((item) => ({
              _id: item._id,
              title: item.name,
              image: `${process.env.REACT_APP_API_BASE_URL}/${item.image}`,
            }));
          setSubcategories(categories);
        }

        const elapsedTime = Date.now() - startTime;
        const remainingTime = MINIMUM_LOAD_TIME - elapsedTime;
        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }
      } catch (err) {
        setError('Failed to load data. Please try again later.');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSubcategories();
  }, [stateSlug]);

  const toggleFavorite = (id) => {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  if (error) {
    return <div className='py-8 text-center text-red-500'>{error}</div>;
  }

  const SkeletonCard = () => (
    <div className='relative w-full animate-pulse rounded-lg border border-[#55E6A5] bg-white p-4 shadow-md'>
      <div className='relative'>
        <div className='h-[220px] w-full rounded-lg bg-gray-200'></div>
        <div className='absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-200'></div>
      </div>
      <div className='mt-2 flex flex-col items-center p-3 text-center'>
        <div className='h-6 w-3/4 rounded bg-gray-200'></div>
      </div>
      <div className='mt-2 h-10 w-full rounded bg-gray-200'></div>
    </div>
  );

  return (
    <div>
      <div className='hero'>
        {loading ? (
          <div className='h-[120px] w-full animate-pulse bg-gray-200 lg:h-[438px]'></div>
        ) : (
          <img
            src={bannerImage || Image}
            alt='breadcrumb background'
            className='hero-img'
          />
        )}
        <ul className='breadcumb'>
          <li>
            <Link to='/domestic'>Domestic</Link>
          </li>
          {stateName && <li>{stateName}</li>}
        </ul>
      </div>
      <div className='py-10'>
        <div className='mb-8 flex flex-col items-center gap-3 md:mb-12'>
          {/* Small Heading with Icon */}
          <div className='flex items-center gap-3'>
            <img
              src={ExploreIcon}
              alt='explore'
              className='h-14 w-14'
            />
            <p className='mont text-2xl font-bold tracking-wide text-gray-900 md:text-3xl'>
              Explore Tour
            </p>
          </div>

          <div className='flex gap-3 text-center'>
            <h3 className='mont relative text-2xl font-bold text-gray-900 md:text-4xl'>
              {stateName ? `${stateName} Packages` : 'Domestic Packages'}
            </h3>
          </div>
        </div>
        <div className='container mx-auto px-4 lg:px-16'>
          <div className='grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
            {loading
              ? Array(4)
                  .fill()
                  .map((_, index) => <SkeletonCard key={index} />)
              : subcategories.map((item) => {
                  const link = stateName
                    ? `/domestic/${slugify(stateName)}/${slugify(item.title)}`
                    : `/domestic/${slugify(item.title)}`;

                  return (
                    <div
                      key={item._id}
                      className='relative w-full rounded-lg border border-[#55E6A5] bg-white p-4 shadow-md'
                    >
                      <div className='relative'>
                        <Link
                          to={link}
                          className='block overflow-hidden rounded-lg'
                        >
                          <img
                            src={item.image || Image}
                            alt={item.title}
                            className='h-[220px] w-full rounded-lg object-cover transition-transform duration-500 ease-in-out hover:scale-110'
                          />
                        </Link>

                        <button
                          onClick={() => toggleFavorite(item._id)}
                          aria-label={`Favorite ${item.title}`}
                          className={`absolute -bottom-3 right-2 flex h-8 w-8 items-center justify-center rounded-full border border-[#55E6A5] shadow-md transition ${
                            favorites.includes(item._id)
                              ? 'bg-[#495BFF] text-white'
                              : 'bg-white text-[#495BFF]'
                          }`}
                        >
                          <i
                            className={`${
                              favorites.includes(item._id) ? 'fas fa-heart' : 'far fa-heart'
                            } text-md`}
                          ></i>
                        </button>
                      </div>
                      <Link to={link}>
                        <div className='mt-2 flex flex-col items-center p-3 text-center'>
                          <h3 className='flex items-start gap-1 font-semibold text-[#495BFF] transition-colors duration-300 ease-in-out hover:text-[#55E6A5]'>
                            <i className='fas fa-map-marker-alt mt-1 text-[#FF0000]'></i>
                            {item.title}
                          </h3>
                        </div>
                      </Link>
                      <div className='flex justify-center'>
                        <Button category={item} />
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>
      {stateName && <CallToAction stateName={stateName} />}
    </div>
  );
};

export default StatePage;
